import React from 'react';
import { Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';

const Schedule = () => {
  const { t } = useTranslation();

  const plans = [
    {
      nameKey: 'plan1Name',
      scheduleKey: 'plan1Schedule',
      priceKey: 'plan1Price',
      features: ['plan1Feature1', 'plan1Feature2'],
      highlighted: false
    },
    {
      nameKey: 'plan2Name',
      scheduleKey: 'plan2Schedule',
      priceKey: 'plan2Price',
      features: ['plan2Feature1', 'plan2Feature2', 'plan2Feature3'],
      highlighted: true
    },
    {
      nameKey: 'plan3Name',
      scheduleKey: 'plan3Schedule',
      priceKey: 'plan3Price',
      features: ['plan3Feature1', 'plan3Feature2', 'plan3Feature3', 'plan3Feature4'],
      highlighted: false
    }
  ];

  return (
    <section id="schedule" className="py-20 bg-gradient-to-br from-pink-50 via-purple-50 to-green-50">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {t('schedule.title')}
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            {t('schedule.subtitle')}
          </p>

          {/* Opening Info */}
          <Card className="border-0 shadow-lg bg-white">
            <CardContent className="p-6">
              <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4 text-center">
                <div>
                  <p className="text-2xl font-bold text-pink-600">{t('schedule.statusValue')}</p>
                  <p className="text-sm text-gray-600 mt-1">{t('schedule.statusLabel')}</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-purple-600">{t('schedule.daysValue')}</p>
                  <p className="text-sm text-gray-600 mt-1">{t('schedule.daysLabel')}</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-600">{t('schedule.hoursValue')}</p>
                  <p className="text-sm text-gray-600 mt-1">{t('schedule.hoursLabel')}</p>
                </div>
                <div>
                  <p className="text-lg font-bold text-gray-700">{t('schedule.closureValue')}</p>
                  <p className="text-sm text-gray-600 mt-1">{t('schedule.closureLabel')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Pricing Plans */}
        <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto mb-8">
          {plans.map((plan, index) => (
            <Card 
              key={index}
              className={`border-0 shadow-xl hover:shadow-2xl transition-all transform hover:-translate-y-2 ${
                plan.highlighted 
                  ? 'bg-gradient-to-br from-pink-500 to-purple-600 text-white ring-4 ring-pink-200' 
                  : 'bg-white'
              }`}
            >
              <CardContent className="p-8">
                {plan.highlighted && (
                  <Badge className="mb-4 bg-white text-pink-600 hover:bg-white">
                    {t('schedule.mostChosen')}
                  </Badge>
                )}
                
                <h3 className={`text-2xl font-bold mb-2 ${plan.highlighted ? 'text-white' : 'text-gray-900'}`}>
                  {t(`schedule.${plan.nameKey}`)}
                </h3>
                
                <p className={`text-sm mb-4 ${plan.highlighted ? 'text-pink-100' : 'text-gray-600'}`}>
                  {t(`schedule.${plan.scheduleKey}`)}
                </p>
                
                <div className="mb-6">
                  <div className="flex items-baseline gap-1">
                    <span className={`text-5xl font-bold ${plan.highlighted ? 'text-white' : 'text-gray-900'}`}>
                      {t(`schedule.${plan.priceKey}`)}
                    </span>
                    <span className={`text-lg ${plan.highlighted ? 'text-pink-100' : 'text-gray-600'}`}>
                      {t('schedule.perMonth')}
                    </span>
                  </div>
                </div>

                <ul className="space-y-3">
                  {plan.features.map((featureKey, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <Check className={`w-5 h-5 flex-shrink-0 mt-0.5 ${plan.highlighted ? 'text-pink-200' : 'text-green-600'}`} />
                      <span className={`text-sm ${plan.highlighted ? 'text-white' : 'text-gray-600'}`}>
                        {t(`schedule.${featureKey}`)}
                      </span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Note */}
        <div className="max-w-4xl mx-auto space-y-4">
          <Card className="border-0 shadow-lg bg-white">
            <CardContent className="p-6">
              <p className="text-center text-gray-700 italic">
                <span className="font-semibold text-pink-600">Nota:</span> {t('schedule.note')}
              </p>
            </CardContent>
          </Card>

          {/* INPS Bonus - Highlighted */}
          <Card className="border-0 shadow-xl bg-gradient-to-r from-green-500 to-teal-600">
            <CardContent className="p-6">
              <div className="flex items-center justify-center gap-3">
                <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center">
                  <Check className="w-7 h-7 text-green-600" />
                </div>
                <p className="text-xl font-bold text-white text-center">
                  {t('schedule.inpsBonus')}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default Schedule;
